import React from 'react';
import SxReveal from './SxReveal';

interface SxSectionProps {
  /** 等宽大写标签。参考站每个 section 都以它开场，建立"技术文档"的阅读节奏 */
  eyebrow?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  align?: 'left' | 'center';
  /** 顶部 hairline 分隔。层级只靠边框，不靠阴影 */
  bordered?: boolean;
  className?: string;
  /** 标题区与内容区的间距，密集网格用 tight */
  gap?: 'normal' | 'tight' | 'wide';
  id?: string;
}

const gapClass: Record<NonNullable<SxSectionProps['gap']>, string> = {
  tight: 'mt-8 md:mt-10',
  normal: 'mt-12 md:mt-16',
  wide: 'mt-16 md:mt-24',
};

/**
 * Section 骨架：统一 eyebrow → 标题 → 描述 → 内容的垂直节奏。
 *
 * 描述文字宽度锁在 58ch 以内。参考站的正文列很窄，
 * 长行会破坏"低密度呼吸感"，也降低可读性。
 */
const SxSection: React.FC<SxSectionProps> = ({
  eyebrow,
  title,
  description,
  children,
  align = 'left',
  bordered = false,
  className = '',
  gap = 'normal',
  id,
}) => {
  const centered = align === 'center';
  const hasHeader = Boolean(eyebrow || title || description);

  return (
    <section id={id} className={`sx-section ${bordered ? 'sx-divide' : ''} ${className}`}>
      <div className="sx-shell">
        {hasHeader && (
          <SxReveal className={centered ? 'text-center' : ''}>
            {eyebrow && <p className="sx-mono mb-4">{eyebrow}</p>}
            {title && <h2 className="sx-h2">{title}</h2>}
            {description && (
              <p
                className={`sx-body-lg mt-5 ${centered ? 'mx-auto' : ''}`}
                style={{ maxWidth: '58ch' }}
              >
                {description}
              </p>
            )}
          </SxReveal>
        )}
        {children && <div className={hasHeader ? gapClass[gap] : ''}>{children}</div>}
      </div>
    </section>
  );
};

export default SxSection;
